import * as React from 'react';
import {
  Link,
  NavLink,
  Outlet,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import {
  ArrowLeft,
  Database,
  Settings as SettingsIcon,
  Sparkles,
  UserCircle,
  Users,
} from 'lucide-react';
import type { EnterpriseDetail } from '@proptr/shared';
import { AppShell } from '../components/layout/AppShell.js';
import { cn, initials } from '../lib/utils.js';
import { client } from '../main.js';

// Context shared with child routes (Outlet) — gives them the loaded agency
// and a setter for optimistic updates after settings PATCHes.
export interface AgencyContext {
  enterpriseId: string;
  agency: EnterpriseDetail | null;
  setAgency: React.Dispatch<React.SetStateAction<EnterpriseDetail | null>>;
}

export function useAgencyContext(): AgencyContext {
  return useOutletContext<AgencyContext>();
}

const NAV: { to: string; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { to: 'proptree', label: 'Proptree settings', icon: Sparkles },
  { to: 'settings', label: 'Agency settings', icon: SettingsIcon },
  { to: 'datasources', label: 'Datasources', icon: Database },
  { to: 'agents', label: 'Agents', icon: UserCircle },
  { to: 'users', label: 'Users', icon: Users },
];

export function AgencyDetailPage() {
  const { enterpriseId } = useParams<{ enterpriseId: string }>();
  const [agency, setAgency] = React.useState<EnterpriseDetail | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!enterpriseId) return;
    let alive = true;
    setError(null);
    client.admin
      .getEnterprise(enterpriseId)
      .then((res) => {
        if (alive) setAgency(res);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      });
    return () => {
      alive = false;
    };
  }, [enterpriseId]);

  const displayName =
    agency?.companyName || agency?.name || (agency ? '—' : 'Loading…');
  const subtitle = agency?.companyName && agency?.name ? agency.name : null;

  const ctx: AgencyContext = {
    enterpriseId: enterpriseId ?? '',
    agency,
    setAgency,
  };

  return (
    <AppShell>
      <div className="space-y-8 pb-12">
        <div className="space-y-3">
          <Link
            to="/agencies"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            All agencies
          </Link>

          <div className="flex items-center gap-4">
            {agency && (
              <AgencyAvatar logo={agency.logo} name={displayName} />
            )}
            <div>
              <div className="eyebrow">Admin / Agencies</div>
              <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
                {displayName}
              </h1>
              <p className="mt-3 font-mono text-[11px] text-muted-foreground">
                {subtitle && (
                  <>
                    <span className="text-foreground">{subtitle}</span>
                    <span className="mx-2 text-muted-foreground/60">·</span>
                  </>
                )}
                ID: <span className="text-foreground">{enterpriseId}</span>
              </p>
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 font-mono text-[11px] text-destructive">
              {error}
            </div>
          )}
        </div>

        <div className="grid gap-8 lg:grid-cols-[220px_1fr]">
          <aside>
            <nav className="flex flex-col gap-0.5">
              <div className="eyebrow px-2 pb-2">Sections</div>
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-2.5 rounded-md px-3 py-2 font-sans text-[13px] font-medium transition-colors',
                      isActive
                        ? 'bg-primary/10 text-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span className="absolute inset-y-1.5 left-0 w-[2px] rounded-full bg-primary" />
                      )}
                      <item.icon
                        className={cn(
                          'h-4 w-4',
                          isActive
                            ? 'text-primary'
                            : 'text-muted-foreground group-hover:text-foreground',
                        )}
                      />
                      <span>{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          </aside>

          <div className="min-w-0">
            <Outlet context={ctx} />
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function AgencyAvatar({
  logo,
  name,
}: {
  logo: string | null;
  name: string;
}) {
  if (logo) {
    return (
      <img
        src={logo}
        alt={name}
        className="h-14 w-14 shrink-0 rounded-md bg-muted object-contain"
      />
    );
  }
  return (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-muted font-mono text-[13px] font-bold uppercase">
      {initials(name)}
    </div>
  );
}
